'use client'

import usePageType from '@/hooks/useLandingPageType'
import { eventTracker } from '@/lib/events/cjrEventTracker'
import { APP_REDIRECTION_SCREENS } from '@/utils/enums'
import { isFromApp } from '@/utils/utils'
import { CourseGoalProps } from '@/widgets/courses/schema'
import { GoalProps, MetaDescriptionProps } from '@/widgets/landing-page-widgets/GoalSelection/schema'
import { Button, Typography } from '@pw-tech/omni-ui'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useState } from 'react'


const BookDemoModal = dynamic(() => import('../../BookDemo'), { ssr: false })

const GoalCard = ({
  data,
  goals,
  cameFrom,
  handleCardClick,
  appRedirectionScreen = APP_REDIRECTION_SCREENS.PAYMENT,
  isOtplessEnabled,
  shouldTriggerTruecaller,
}: {
  data: GoalProps | CourseGoalProps
  goals: GoalProps[] | CourseGoalProps[]
  cameFrom?: 'course' | 'landing'
  handleCardClick?: (d: CourseGoalProps) => void
  appRedirectionScreen?: APP_REDIRECTION_SCREENS
  isOtplessEnabled: boolean
  shouldTriggerTruecaller?: boolean
}) => {
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false)
  const { currentPageType, fullPath } = usePageType()

  const isFromCourse = cameFrom === 'course'

  const handleOpenBookDemoModal = () => {
    eventTracker.bookADemoClick({
      clickFrom: 'cohorts_section',
      selected_cohort: data.title,
      came_from: currentPageType,
      page_link: fullPath as string,
    })
    setIsBookDemoModalOpen(true)
  }

  return (
    <>
      <div
        className="flex flex-col p-4 rounded-2xl gap-3 border border-[#EFEFEF] justify-between h-full"
        style={{
          background: data.color,
          boxShadow: '0px 1px 8px 0px #00000014',
          minHeight: '430px',
        }}
        tabIndex={0}
        role='button'
        onClick={() => (isFromCourse ? null : handleOpenBookDemoModal())}
      >
  <div className="h-[200px] w-full rounded-lg overflow-hidden relative">
          <Image
            src={data?.thumbnail}
            fill
            className='object-cover object-center'
            alt='goals-thumbnail'
            loading='lazy'
            sizes='(max-width: 768px) 100vw, 33vw'
            priority={false}
          />
        </div>

  <div className="flex flex-col gap-[6px] md:gap-2">
          <Typography variant='heading3' weight='bold' color='#1b2124'>
            {data.title}
          </Typography>
          <Typography variant='regular' color='#3d3d3d'>
            {data.description}
          </Typography>
        </div>

  <div className="flex flex-col gap-[6px] md:gap-2">
          {(data?.metaDescription || []).map((meta: MetaDescriptionProps) => {
            return (
              <div key={meta.title} className="flex flex-row md:gap-4 gap-2 items-center">
                <div className="border-[0.67px] border-[#CCCCCC33] w-8 h-8 rounded-lg flex items-center justify-center">
                  <Image src={meta.icon} width={20} height={20} alt='icon' loading='lazy' />
                </div>
                <Typography component='p' color='#3d3d3d' className="flex-1">
                  {meta.title}
                </Typography>
              </div>
            )
          })}
          <Button
            className="mt-2 bg-gradient-to-b from-[#ff9d5b] to-[#ff6d0a]"
            onClick={() => (isFromCourse ? handleCardClick?.(data as CourseGoalProps) : null)}
          >
            {isFromCourse ? 'Explore Now' : 'Book a Demo'}
          </Button>
        </div>
      </div>

      {isBookDemoModalOpen && (
        <BookDemoModal
          open={isBookDemoModalOpen}
          onClose={() => setIsBookDemoModalOpen(false)}
          activeState='book_demo'
          activeGoal={data as GoalProps}
          goals={goals as GoalProps[]}
          appRedirectionScreen={appRedirectionScreen}
          isOtplessEnabled={isOtplessEnabled}
          shouldTriggerTruecaller={shouldTriggerTruecaller || false}
          pageSource={isFromApp() ? 'APP' : 'LANDING'}
        />
      )}
    </>
  )
}

export default GoalCard
